import { AxiosResponse } from 'axios'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDeleteForever } from 'react-icons/md'
import { useLocation } from 'react-router-dom'
import { apiClient } from '../../api'
import { useAttachments, useDeleteAttachment } from '../../hooks/useAttachments'
import { formatRelativeTime } from '../../utils/formatDate'
import { formatFileSize } from '../../utils/numberFormat'
import { ConfirmDialog } from '../common'
import './AttachmentList.css'
import { ResumableUploadButton } from './ResumableUploadButton'

interface AttachmentListProps {
  cardId: number
}

/**
 * AttachmentList Component
 * Displays list of attachments with download and delete functionality
 * Requirements: 12.4, 12.5, 5.3, 5.5
 */
export const AttachmentList: React.FC<AttachmentListProps> = ({ cardId }) => {
  const { t } = useTranslation()
  const { data: attachments = [], isLoading } = useAttachments(cardId)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const deleteAttachment = useDeleteAttachment()
  const location = useLocation()
  const isCalendarRoute = location.pathname.includes('calendar')

  const getFileIcon = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase()
    switch (ext) {
      case 'pdf':
        return '📄'
      case 'doc':
      case 'docx':
        return '📝'
      case 'xls':
      case 'xlsx':
        return '📊'
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️'
      case 'zip':
      case 'rar':
        return '📦'
      default:
        return '📎'
    }
  }

  const handleDownload = async (fileUrl: string, originalFileName: string) => {
    try {
      // fileUrl dan oxirgi qismni ajratib olish
      const storedFileName = fileUrl.split('/').pop()
      if (!storedFileName) throw new Error('Invalid file URL')

      const response: AxiosResponse<Blob> = await apiClient.get(
        `/files/download/${storedFileName}`,
        { responseType: 'blob' }
      )

      // Faylni browserga yuklash
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', originalFileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      console.error('File download error:', error)
      alert(error?.response?.data?.message || 'Download failed')
    }
  }

  const handleDeleteClick = (id: number) => {
    setDeleteId(id)
  }

  const handleDeleteConfirm = async () => {
    if (deleteId) {
      await deleteAttachment.mutateAsync(deleteId)
      setDeleteId(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteId(null)
  }

  if (isLoading) {
    return (
      <div className="attachment-list">
        <p className="attachment-list__empty-message">{t('common.loading')}</p>
      </div>
    )
  }

  return (
    <>
      <div className="attachment-list">
        <div className="attachment-list__header">
          {!isCalendarRoute && <ResumableUploadButton cardId={cardId} />}
        </div>

        {attachments.length === 0 ? (
          <p className="attachment-list__empty-message">
            {t('card.noAttachments')}
          </p>
        ) : (
          <div className="attachment-list__items">
            {attachments.map((attachment) => (
              <div key={attachment.id} className="attachment-item">
                <div className="attachment-item__icon">
                  {getFileIcon(attachment.fileName)}
                </div>
                <div className="attachment-item__info">
                  <div
                    className="attachment-item__name"
                    title={attachment.fileName}
                  >
                    {attachment.fileName}
                  </div>
                  <div className="attachment-item__meta">
                    <span className="attachment-item__size">
                      {formatFileSize(attachment.fileSize)}
                    </span>
                    <span className="attachment-item__separator">•</span>
                    <span className="attachment-item__date">
                      {formatRelativeTime(attachment.createdAt)}
                    </span>
                    <span className="attachment-item__separator">•</span>
                    <span className="attachment-item__uploader">
                      {attachment.uploaderName}
                    </span>
                  </div>
                </div>
                <div className="attachment-item__actions">
                  <button
                    type="button"
                    className="attachment-item__button"
                    onClick={() =>
                      handleDownload(attachment.fileUrl, attachment.fileName)
                    }
                    aria-label={t('card.download')}
                    title={t('card.download')}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 2V10M8 10L5 7M8 10L11 7M2 14H14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  {!isCalendarRoute && (
                    <button
                      type="button"
                      className="attachment-item__button attachment-item__button--delete"
                      onClick={() => handleDeleteClick(attachment.id)}
                      aria-label={t('common.delete')}
                      title={t('common.delete')}
                    >
                      <MdDeleteForever />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={deleteId !== null}
        title={t('card.deleteCard')}
        message={t('card.deleteCardConfirm', { title: t('card.attachments') })}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
      />
    </>
  )
}
